import * as React from "react";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";

import logo from "../../assets/new_full_logo_transparent.png";

import { NavLink } from "react-router-dom";


const pages = [
  { to: "/", name: "Home" },
  { to: "/about", name: "About Us" },
  { to: "/science-classes", name: "Science" },
  { to: "/robotics-for-children", name: "Robotics" },
  {
    to: "/public-speaking-for-children",
    name: "Public Speaking",
  },
  { to: "/finance-for-kids", name: "Finance" },
  {
    to: "/3d-printing-for-children",
    name: "3D Printing",
  },
];


function Nav() {

  const [anchorElNav, setAnchorElNav] =
    React.useState(null);

  const [scrolling, setScrolling] =
    React.useState(false);


  /* ============================
     SCROLL TO TOP
  ============================ */

  const scrollToTop = React.useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);


  /* ============================
     MOBILE MENU
  ============================ */

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };


  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };


  const handleNavigation = () => {
    handleCloseNavMenu();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };


  /* ============================
     SCROLL DETECTION

     requestAnimationFrame prevents
     unnecessary work during scrolling
  ============================ */

  React.useEffect(() => {

    let ticking = false;

    const handleScroll = () => {

      if (!ticking) {

        window.requestAnimationFrame(() => {

          const shouldBeScrolling =
            window.scrollY > 30;

          setScrolling((previous) =>
            previous === shouldBeScrolling
              ? previous
              : shouldBeScrolling
          );

          ticking = false;

        });

        ticking = true;
      }
    };


    handleScroll();


    window.addEventListener(
      "scroll",
      handleScroll,
      { passive: true }
    );


    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };

  }, []);


  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}

        sx={{
          /*
           * Matches the new homepage.
           *
           * Starts softly tinted and becomes
           * slightly more solid after scrolling.
           */

          background: scrolling
            ? "rgba(255, 255, 255, 0.97)"
            : `
              linear-gradient(
                90deg,
                #fff8fc 0%,
                #faf7ff 40%,
                #f5f9ff 72%,
                #f4fcfb 100%
              )
            `,

          color: "#202635",

          borderBottom:
            "1px solid rgba(100, 90, 140, 0.08)",

          boxShadow: scrolling
            ? "0 8px 28px rgba(50, 45, 85, 0.09)"
            : "none",

          transition:
            "background 0.25s ease, box-shadow 0.25s ease",

          /*
           * Small multicolour accent matching
           * the homepage headline.
           */

          "&::after": {
            content: '""',

            position: "absolute",
            bottom: 0,
            left: 0,

            width: "100%",
            height: "2px",

            background: `
              linear-gradient(
                90deg,
                #e73392,
                #8b4bd9,
                #3c83e6,
                #1e9f91
              )
            `,

            opacity: scrolling
              ? 0.65
              : 0.32,

            transition: "opacity 0.25s ease",
          },
        }}
      >

        <Container maxWidth="xl">

          <Toolbar
            disableGutters

            sx={{
              minHeight: {
                xs: "68px",
                md: "76px",
              },

              gap: {
                xs: 1,
                md: 2.5,
              },
            }}
          >


            {/* ============================
                LOGO
            ============================ */}

            <NavLink
              to="/"
              onClick={scrollToTop}

              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >

                <img
                  src={logo}

                  alt="Xperio Learning"

                  width="230"
                  height="72"

                  loading="eager"
                  fetchPriority="high"
                  decoding="async"

                  style={{
                    display: "block",
                    width: "auto",
                    height: "auto",
                    maxHeight: "58px",
                    maxWidth: "230px",
                    objectFit: "contain",
                  }}
                />

              </Box>

            </NavLink>



            {/* ============================
                DESKTOP MENU
            ============================ */}

            <Box
              sx={{
                flexGrow: 1,

                display: {
                  xs: "none",
                  md: "flex",
                },

                alignItems: "center",
                justifyContent: "center",

                gap: {
                  md: "4px",
                  lg: "10px",
                  xl: "18px",
                },
              }}
            >

              {pages.map((page) => (

                <NavLink
                  key={page.to}

                  to={page.to}

                  end={page.to === "/"}

                  style={{
                    textDecoration: "none",
                  }}
                >

                  {({ isActive }) => (

                    <Button
                      onClick={handleCloseNavMenu}

                      sx={{
                        position: "relative",

                        px: {
                          md: 1,
                          lg: 1.35,
                        },

                        py: 1,

                        minWidth: "auto",

                        color: isActive
                          ? "#7246c7"
                          : "#343948",

                        fontSize: {
                          md: "0.76rem",
                          lg: "0.82rem",
                          xl: "0.86rem",
                        },

                        fontWeight: isActive
                          ? 800
                          : 650,

                        textTransform: "none",

                        whiteSpace: "nowrap",

                        borderRadius: "10px",

                        transition:
                          "all 0.2s ease",

                        "&:hover": {

                          color: "#7246c7",

                          backgroundColor:
                            "rgba(123, 80, 190, 0.06)",

                          transform:
                            "translateY(-1px)",
                        },


                        /* Active page underline */

                        "&::after": {

                          content: '""',

                          position: "absolute",

                          left: "50%",
                          bottom: "1px",

                          width: isActive
                            ? "18px"
                            : "0px",

                          height: "2px",

                          borderRadius: "10px",

                          transform:
                            "translateX(-50%)",

                          background:
                            "linear-gradient(90deg, #e73392, #764de0)",

                          transition:
                            "width 0.2s ease",
                        },
                      }}
                    >

                      {page.name}

                    </Button>

                  )}

                </NavLink>

              ))}

            </Box>



            {/* ============================
                DESKTOP ENROLL CTA
            ============================ */}

            <Box
              sx={{
                display: {
                  xs: "none",
                  lg: "flex",
                },

                alignItems: "center",

                flexShrink: 0,
              }}
            >

              <NavLink
                to="/enroll"

                style={{
                  textDecoration: "none",
                }}
              >

                <Button
                  sx={{
                    px: 2.1,
                    py: 1,

                    borderRadius: "12px",

                    color: "#ffffff",

                    fontWeight: 800,

                    textTransform: "none",

                    background: `
                      linear-gradient(
                        135deg,
                        #e73392,
                        #8b4bd9,
                        #476de1
                      )
                    `,

                    boxShadow:
                      "0 7px 18px rgba(131, 73, 206, 0.22)",

                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease",

                    "&:hover": {

                      background: `
                        linear-gradient(
                          135deg,
                          #e73392,
                          #8b4bd9,
                          #476de1
                        )
                      `,

                      transform:
                        "translateY(-2px)",

                      boxShadow:
                        "0 10px 24px rgba(131, 73, 206, 0.30)",
                    },
                  }}
                >

                  Enroll Now

                </Button>

              </NavLink>

            </Box>



            {/* ============================
                MOBILE MENU BUTTON
            ============================ */}

            <Box
              sx={{
                display: {
                  xs: "flex",
                  md: "none",
                },

                alignItems: "center",

                marginLeft: "auto",
              }}
            >

              <IconButton
                size="medium"

                aria-label="Open navigation menu"

                aria-controls={
                  anchorElNav
                    ? "mobile-nav-menu"
                    : undefined
                }

                aria-haspopup="true"

                aria-expanded={
                  anchorElNav
                    ? "true"
                    : undefined
                }

                onClick={handleOpenNavMenu}

                sx={{
                  width: "44px",
                  height: "44px",

                  borderRadius: "12px",

                  color: "#7047bf",

                  border:
                    "1px solid rgba(116, 71, 199, 0.10)",

                  background:
                    "rgba(255,255,255,0.65)",

                  "&:hover": {
                    background:
                      "rgba(116, 71, 199, 0.07)",
                  },
                }}
              >

                <MenuIcon />

              </IconButton>

            </Box>


          </Toolbar>

        </Container>

      </AppBar>



      {/* ============================
          MOBILE MENU
      ============================ */}

      <Menu
        id="mobile-nav-menu"

        anchorEl={anchorElNav}

        open={Boolean(anchorElNav)}

        onClose={handleCloseNavMenu}

        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}

        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}

        keepMounted

        slotProps={{
          paper: {

            sx: {
              mt: 1.2,

              width: "min(310px, calc(100vw - 28px))",

              borderRadius: "18px",

              overflow: "hidden",

              border:
                "1px solid rgba(100, 90, 140, 0.10)",

              background: "#ffffff",

              boxShadow:
                "0 18px 45px rgba(45, 40, 75, 0.16)",

              p: 1,
            },
          },
        }}
      >

        <Box
          sx={{
            px: 1.5,
            pt: 1,
            pb: 0.7,
          }}
        >

          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 800,

              color: "#9692a2",

              textTransform: "uppercase",

              letterSpacing: "1.5px",
            }}
          >

            Explore Xperio

          </Typography>

        </Box>


        {pages.map((page) => (

          <NavLink
            key={page.to}

            to={page.to}

            end={page.to === "/"}

            onClick={handleNavigation}

            style={{
              textDecoration: "none",
            }}
          >

            {({ isActive }) => (

              <MenuItem
                sx={{
                  minHeight: "45px",

                  borderRadius: "11px",

                  mb: "2px",

                  px: 1.5,

                  color: isActive
                    ? "#7447c7"
                    : "#333846",

                  backgroundColor: isActive
                    ? "rgba(116, 71, 199, 0.07)"
                    : "transparent",

                  "&:hover": {
                    backgroundColor:
                      "rgba(116, 71, 199, 0.06)",
                  },
                }}
              >

                <Typography
                  sx={{
                    fontWeight: isActive
                      ? 800
                      : 600,

                    fontSize: "0.94rem",
                  }}
                >

                  {page.name}

                </Typography>

              </MenuItem>

            )}

          </NavLink>

        ))}


        {/* Mobile CTA */}

        <Box
          sx={{
            px: 0.7,
            pt: 1,
            pb: 0.5,
          }}
        >

          <NavLink
            to="/enroll"

            onClick={handleNavigation}

            style={{
              textDecoration: "none",
            }}
          >

            <Button
              fullWidth

              sx={{
                py: 1.2,

                borderRadius: "12px",

                color: "white",

                fontWeight: 800,

                textTransform: "none",

                background: `
                  linear-gradient(
                    135deg,
                    #e73392,
                    #8b4bd9,
                    #476de1
                  )
                `,

                "&:hover": {

                  background: `
                    linear-gradient(
                      135deg,
                      #e73392,
                      #8b4bd9,
                      #476de1
                    )
                  `,
                },
              }}
            >

              Enroll Now →

            </Button>

          </NavLink>

        </Box>

      </Menu>



      {/* ============================
          NAVBAR SPACER
      ============================ */}

      <Box
        sx={{
          height: {
            xs: "68px",
            md: "76px",
          },
        }}
      />

    </>
  );
}

export default Nav;